import { useCallback, useRef, useState } from 'react';
import type { AssistantLanguage } from '@chat-emp/shared';

import type { DraftTranslation } from '../conversation/AssistantCard.js';
import { translateText } from '../lib/facade.js';

interface Source {
  auditId?: string;
  /** The language the drafts were written in. */
  language: AssistantLanguage;
  replies: string[];
}

interface Shown {
  auditId: string;
  /** Per draft: the language it is shown in. */
  languages: AssistantLanguage[];
  pending: number | null;
  failed: number | null;
  /** Per language, per draft index: only the drafts someone asked for. */
  translated: Partial<Record<AssistantLanguage, string[]>>;
}

interface UseDraftTranslation {
  replies: string[];
  /** Per draft: the language it is shown in. */
  languages: AssistantLanguage[];
  /** Absent while there is no stored reading with drafts to translate. */
  translation?: DraftTranslation;
}

/** Each draft of one reading in whichever language the employer picked for it; a new reading starts over. */
export function useDraftTranslation(source: Source): UseDraftTranslation {
  const [held, setHeld] = useState<Shown | null>(null);
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const heldRef = useRef(held);
  heldRef.current = held;

  const translate = useCallback((index: number, to: AssistantLanguage) => {
    const { auditId, language: original, replies } = sourceRef.current;
    if (!auditId) return;
    const current: Shown =
      heldRef.current?.auditId === auditId
        ? heldRef.current
        : {
            auditId,
            languages: replies.map(() => original),
            pending: null,
            failed: null,
            translated: {},
          };
    const showIn = (prev: Shown): Shown => ({
      ...prev,
      languages: prev.languages.map((l, i) => (i === index ? to : l)),
      pending: null,
      failed: null,
    });
    if (to === original || current.translated[to]?.[index] !== undefined) {
      setHeld(showIn(current));
      return;
    }
    setHeld({ ...current, pending: index, failed: null });
    const stillAsked = (prev: Shown | null): prev is Shown =>
      prev?.auditId === auditId && prev.pending === index;
    translateText({ language: to, text: replies[index]! })
      .then((res) => {
        setHeld((prev) => {
          if (!stillAsked(prev)) return prev;
          const inLanguage = [...(prev.translated[to] ?? [])];
          inLanguage[index] = res.text;
          return showIn({ ...prev, translated: { ...prev.translated, [to]: inLanguage } });
        });
      })
      .catch(() => {
        setHeld((prev) => (stillAsked(prev) ? { ...prev, pending: null, failed: index } : prev));
      });
  }, []);

  const shown = source.auditId && held?.auditId === source.auditId ? held : null;
  const languages = source.replies.map((_, i) => shown?.languages[i] ?? source.language);
  const replies = source.replies.map((reply, i) => shown?.translated[languages[i]!]?.[i] ?? reply);

  if (!source.auditId || source.replies.length === 0) {
    return { replies, languages };
  }
  return {
    replies,
    languages,
    translation: {
      languages,
      pending: shown?.pending ?? null,
      failed: shown?.failed ?? null,
      onTranslate: translate,
    },
  };
}
